/**
 * The sticky kicker on the canvas (document themes, 2d) — the pure part.
 *
 * The kicker names where you are once the current chapter's heading has
 * scrolled away: the chapter (a tab with its number), its name, and the
 * subsection you are in. A chapter is a top-level heading; the number is the
 * one Index colours by (look.ts `documentChapters`), so an unnumbered chapter
 * has no tab, and an appendix reads A, B.
 */
import { documentChapters, headingLevel, isAppendixMarker } from "./look";

interface BlockLike {
  id: string;
  type: string;
  content?: Record<string, unknown> | null;
}

export interface KickerHeading {
  id: string;
  title: string;
  level: number;
  /** "3", "3.2", "A"; empty for an unnumbered heading or anything deeper. */
  number: string;
}

export interface KickerChapter extends KickerHeading {
  /** The headings under it, until the next top-level one. */
  subs: KickerHeading[];
}

const text = (b: BlockLike) =>
  (typeof b.content?.text === "string" ? b.content.text.replace(/<[^>]*>/g, "").trim() : "") || "Untitled";

/** The document's top-level headings, each with what sits under it. */
export function kickerChapters(blocks: readonly BlockLike[]): KickerChapter[] {
  const labels = new Map(documentChapters(blocks).map((c) => [c.id, c.label]));
  const out: KickerChapter[] = [];
  let current: KickerChapter | null = null;
  let sub = 0;
  for (const b of blocks) {
    if (isAppendixMarker(b)) continue;
    if (b.type !== "heading") continue;
    const level = headingLevel(b);
    if (level === 1) {
      current = { id: b.id, title: text(b), level, number: labels.get(b.id) ?? "", subs: [] };
      sub = 0;
      out.push(current);
    } else if (current) {
      const numbered = b.content?.numbered !== false;
      if (level === 2 && numbered) sub += 1;
      current.subs.push({
        id: b.id,
        title: text(b),
        level,
        number: level === 2 && numbered && current.number ? `${current.number}.${sub}` : "",
      });
    }
  }
  return out;
}

/** Fewer than two top-level sections: nothing to find your way between. */
export function kickerApplies(chapters: readonly KickerChapter[]): boolean {
  return chapters.length >= 2;
}

/** Where a heading is, relative to the top of the scrolling canvas. */
export interface HeadingBox {
  top: number;
  bottom: number;
}

export interface KickerPlace {
  chapter: KickerChapter;
  sub: KickerHeading | null;
  /** True once the chapter's own heading has scrolled out of view. */
  out: boolean;
}

/**
 * Which chapter and subsection the reader is in: the last heading whose top
 * has reached `threshold` (the band the pill covers). Null above the first
 * chapter.
 */
export function kickerPlace(
  chapters: readonly KickerChapter[],
  boxOf: (id: string) => HeadingBox | null,
  threshold: number,
): KickerPlace | null {
  let place: KickerPlace | null = null;
  for (const c of chapters) {
    const box = boxOf(c.id);
    if (!box) continue;
    if (box.top > threshold) break;
    place = { chapter: c, sub: null, out: box.bottom <= 0 };
  }
  if (!place) return null;
  for (const s of place.chapter.subs) {
    const box = boxOf(s.id);
    if (!box) continue;
    if (box.top > threshold) break;
    place.sub = s;
  }
  return place;
}
